import React, { useState } from 'react'
import { Tabs, TabPanel } from '@paypalcorp/pp-react'
import { isNil } from 'lodash'
import useBreakpoint from '../lib/useBreakpoint'
import OfferDetails from '../offer-details/OfferDetails'
import NoOfferDetails from '../offer-details/NoOfferDetails'
import {
  OfferTabsType,
  HoneyOfferType,
  UrlQueryParamsType
} from '../types/StorePageType'
import { sendFptiClick } from '../lib/fpti'
import {
  SALE_TYPE,
  COUPON_TYPE,
  REVEALED_COUPON_TYPE,
  SHOPPING_MERCHANT_TAB_ALL_PRESSED,
  SHOPPING_MERCHANT_TAB_DEALS_PRESSED,
  SHOPPING_MERCHANT_TAB_CODES_PRESSED,
  SHOPPING_MERCHANT_DEAL_PRESSED,
  SHOPPING_MERCHANT_CODE_PRESSED
} from '../lib/client-constants'
import { getOfferData } from '../utils/get-offer-details'
import useWorldReady from '../lib/useWorldReady'

interface Props {
  allOffers: OfferTabsType
  affiliateVisited: boolean
  encryptedAccountNumber?: string
  urlProperties: UrlQueryParamsType
  fptiSessionId: string
  storeId: string
}

interface Tab {
  label: string
  id: 'group1-tab1' | 'group1-tab2' | 'group1-tab3'
  panelId: 'panel1' | 'panel2' | 'panel3'
  eventName:
    | typeof SHOPPING_MERCHANT_TAB_ALL_PRESSED
    | typeof SHOPPING_MERCHANT_TAB_CODES_PRESSED
    | typeof SHOPPING_MERCHANT_TAB_DEALS_PRESSED
}

const OfferTabs = ({
  allOffers,
  affiliateVisited,
  encryptedAccountNumber,
  urlProperties,
  fptiSessionId,
  storeId
}: Props) => {
  const { formatMessage } = useWorldReady()
  const [currentTab, setCurrentTab] = useState(0)
  const breakpoint = useBreakpoint()
  const totalNumOfOffers = allOffers.honeyAll.length
  const numOfCoupons = allOffers.honeyCoupons.length
  const numOfDeals = allOffers.honeyDeals.length
  const couponTab = formatMessage('main.storePage.tabs.codes', { numOfCoupons })
  const dealsTab = formatMessage('main.storePage.tabs.deals', { numOfDeals })
  const allOffersTab = formatMessage('main.storePage.tabs.all', {
    all: totalNumOfOffers
  })
  const tabs: Tab[] = [
    {
      label: allOffersTab,
      id: 'group1-tab1',
      panelId: 'panel1',
      eventName: SHOPPING_MERCHANT_TAB_ALL_PRESSED
    },
    {
      label: couponTab,
      id: 'group1-tab2',
      panelId: 'panel2',
      eventName: SHOPPING_MERCHANT_TAB_CODES_PRESSED
    },
    {
      label: dealsTab,
      id: 'group1-tab3',
      panelId: 'panel3',
      eventName: SHOPPING_MERCHANT_TAB_DEALS_PRESSED
    }
  ]

  const getOffersDetails = function get(offers: HoneyOfferType[]) {
    return offers.map((offer: HoneyOfferType, idx: number) => {
      const offerData = getOfferData(
        offer,
        allOffers.storeUrl,
        fptiSessionId,
        storeId
      )

      const FPTI_CLICK_EVENT =
        offer.type === 'COUPON'
          ? SHOPPING_MERCHANT_DEAL_PRESSED
          : SHOPPING_MERCHANT_CODE_PRESSED

      const COUPON_OFFER_TYPE = affiliateVisited
        ? REVEALED_COUPON_TYPE
        : COUPON_TYPE
      const OFFER_TYPE = offer.type === 'COUPON' ? COUPON_OFFER_TYPE : SALE_TYPE

      return (
        <li
          data-cy={OFFER_TYPE}
          key={idx}
          onClick={() => {
            sendFptiClick({
              event_name: FPTI_CLICK_EVENT,
              offer_id: offer.dealId,
              rank: ++idx
            })
          }}
        >
          <OfferDetails
            offerData={offerData}
            offerType={OFFER_TYPE}
            breakpoint={breakpoint}
            urlProperties={urlProperties}
            encryptedAccountNumber={encryptedAccountNumber}
          />
        </li>
      )
    })
  }

  let offerCoupons = <NoOfferDetails offerType="codes" />

  if (numOfCoupons) {
    const mappedCoupons = getOffersDetails(allOffers.honeyCoupons)
    offerCoupons = <>{mappedCoupons}</>
  }

  let offerDeals = <NoOfferDetails offerType="deals" />
  if (numOfDeals) {
    const mappedDeals = getOffersDetails(allOffers.honeyDeals)
    offerDeals = <>{mappedDeals}</>
  }

  let offerAll = <NoOfferDetails offerType="codesAndDeals" />
  if (totalNumOfOffers) {
    const mappedAllOffers = getOffersDetails(allOffers.honeyAll)
    offerAll = <>{mappedAllOffers}</>
  }

  return (
    <div data-cy="offerTabsContainer">
      <Tabs
        name="OfferTabs"
        selectedTabIdx={currentTab}
        tabs={tabs}
        onClick={(_e, tabIdx) => {
          if (!isNil(tabIdx) && !isNaN(tabIdx)) {
            setCurrentTab(tabIdx)
            if (currentTab !== tabIdx) {
              sendFptiClick({
                event_name: tabs[tabIdx].eventName
              })
            }
          }
        }}
      >
        <TabPanel tabId="group1" id="panel1" className="offer-tabs__content">
          <ul
            className="store-page__offer-list"
            data-cy="offer-tabs__content--all"
          >
            {offerAll}
          </ul>
        </TabPanel>

        <TabPanel tabId="group1" id="panel2" className="offer-tabs__content">
          <ul
            className="store-page__offer-list"
            data-cy="offer-tabs__content--coupons"
          >
            {offerCoupons}
          </ul>
        </TabPanel>

        <TabPanel tabId="group1" id="panel3" className="offer-tabs__content">
          <ul
            className="store-page__offer-list"
            data-cy="offer-tabs__content--deals"
          >
            {offerDeals}
          </ul>
        </TabPanel>
      </Tabs>
    </div>
  )
}

export default OfferTabs
